import React, { Component } from 'react';

import Constants from '../Constants';
import ScoreboardRow from './ScoreboardRow';
import Seasons from '../Seasons';
import Utils from '../Utils';

import '../styles/Scoreboard.css';

class Scoreboard extends Component {
  constructor(props) {
    super(props);

    // A period is chosen on two axes rather than stepped through in a ring, so
    // what is held is the pair and not a position in a list.
    this.state = {
      span: Constants.SCORE_SPAN_DAY,
      recency: Constants.SCORE_RECENCY_THIS
    };
  }

  // A season the site has not reached yet has no board to show. Its option is
  // disabled rather than dropped: a dead option says a previous season is
  // coming, where a missing one says nothing at all.
  isRecencyAvailable = (span, recency) => {
    const periodScores = this.props.scores[Constants.SCORE_PERIODS_BY_SPAN[span][recency]];
    return !!periodScores && !periodScores.unavailable;
  }

  // Before the first season began neither season board existed, so the span
  // itself had nothing to offer and is disabled whole.
  isSpanAvailable = (span) => {
    return Constants.SCORE_RECENCIES.some((recency) => this.isRecencyAvailable(span, recency));
  }

  // What the periods' availability does to a selection kept from an earlier
  // render. It shrinks and grows as seasons begin, so a span with nothing in it
  // falls back to the first span that has something, and a recency the chosen
  // span cannot offer falls back within that span. The board is never left
  // holding a period it cannot draw.
  getSelection = () => {
    const span = this.isSpanAvailable(this.state.span)
      ? this.state.span
      : Constants.SCORE_SPANS.find(this.isSpanAvailable) || Constants.SCORE_SPAN_DAY;

    const recency = this.isRecencyAvailable(span, this.state.recency)
      ? this.state.recency
      : Constants.SCORE_RECENCIES.find((candidate) => this.isRecencyAvailable(span, candidate))
        || Constants.SCORE_RECENCY_THIS;

    return { span, recency };
  }

  // A period that includes today, seen by somebody who has not finished today's
  // daily. Unlike an unavailable one its option stays live: the period exists,
  // and selecting it is how the board gets to say what opens it. Disabling it
  // instead would leave three dead buttons and nowhere to explain them.
  isPeriodLocked = (period) => {
    return !!this.props.scores[period].locked;
  }

  renderLockedMessage = (period) => {
    const loggedIn = !!this.props.loggedInPlayerName;
    const { action, remainder } = Utils.assembleLockedPeriodSentence(period, loggedIn);
    const onClick = loggedIn ? this.props.onPlayDailyClick : this.props.onLoginClick;

    return (
      <div className="scoreboard-locked">
        {onClick
          ? <button type="button" className="scoreboard-locked-action" onClick={onClick}>
              {action}
            </button>
          : action
        }
        {remainder}
      </div>
    );
  }

  getPeriod = () => {
    const { span, recency } = this.getSelection();

    return Constants.SCORE_PERIODS_BY_SPAN[span][recency];
  }

  // The season options are named by number, so a standing is read under the
  // season it belongs to. The season before the first has no number to be called
  // by, and takes the axis's own word instead.
  getRecencyLabel = (span, recency) => {
    if (span !== Constants.SCORE_SPAN_SEASON) {
      return Constants.SCORE_RECENCY_LABELS[span][recency];
    }

    const seasonsBack = recency === Constants.SCORE_RECENCY_LAST ? 1 : 0;
    const seasonNumber = Seasons.getCurrentSeasonNumber() - seasonsBack;

    return seasonNumber >= 1 ? `Season ${seasonNumber}` : Constants.SCORE_RECENCY_NAMES[recency];
  }

  handleSpanClick = (e) => {
    this.setState({ span: e.currentTarget.dataset.value });
  }

  handleRecencyClick = (e) => {
    this.setState({ recency: e.currentTarget.dataset.value });
  }

  handleRowClick = (name) => {
    const period = this.getPeriod();
    let date;
    if (period === Constants.SCORES_TODAY) {
      date = Utils.getDateStringToday();
    } else if (period === Constants.SCORES_YESTERDAY) {
      date = Utils.getDateStringYesterday();
    } else {
      return;
    }

    this.props.onClick(name, date);
  }

  // The board shows the top ten, but a player outside it still wants to know
  // where they stand, so their own standing follows the board. Nothing is shown
  // for a player who has not played the period: there is no score to give.
  getOwnStanding = () => {
    const playerName = this.props.loggedInPlayerName;
    if (!playerName) {
      return null;
    }

    const period = this.props.scores[this.getPeriod()];
    const onBoard = period.scores && period.scores.some((score) => score.name === playerName);
    if (onBoard) {
      return null;
    }

    return (period.standings || []).find((score) => score.name === playerName) || null;
  }

  renderOption = (value, label, selected, available, onClick) => {
    return (
      <button
        key={`scoreboard_option_${value}`}
        type="button"
        className={`scoreboard-selector-option${selected ? " scoreboard-selector-option-selected" : ""}`}
        data-value={value}
        aria-pressed={selected}
        disabled={!available}
        onClick={onClick}
      >
        {label}
      </button>
    );
  }

  renderRow = (score, index) => {
    const period = this.getPeriod();
    const clickable = period === Constants.SCORES_YESTERDAY || (period === Constants.SCORES_TODAY && this.props.clickableToday);
    const today = period === Constants.SCORES_TODAY;

    const yesterdayRank = today && score.name in this.props.starMap ? this.props.starMap[score.name] : null;
    const lastWeekRank = today && score.name in this.props.medalMap ? this.props.medalMap[score.name] : null;
    const lastSeasonRank = today && score.name in this.props.trophyMap ? this.props.trophyMap[score.name] : null;

    // A week's row lists the individual days behind it; a season's is
    // six weeks long, so it summarises instead and needs no start date.
    let rangeStartDate = null;
    if (period === Constants.SCORES_THIS_WEEK) {
      rangeStartDate = Utils.getDateStringMostRecentMonday();
    } else if (period === Constants.SCORES_LAST_WEEK) {
      rangeStartDate = Utils.getDateStringTwoMondaysAgo();
    }

    return (
      <ScoreboardRow
        key={`scoreboard_row_${index}`}
        index={index}
        own={score.name === this.props.loggedInPlayerName}
        score={score}
        rangeStartDate={rangeStartDate}
        rangeDayCount={Constants.SCORE_PERIOD_DAY_COUNTS[period]}
        yesterdayRank={yesterdayRank}
        lastWeekRank={lastWeekRank}
        lastSeasonRank={lastSeasonRank}
        seasonsStarted={Seasons.hasStarted(Utils.getDateStringToday())}
        onClick={clickable ? this.handleRowClick : null}
      />
    );
  }

  render() {
    const { span, recency } = this.getSelection();
    const period = Constants.SCORE_PERIODS_BY_SPAN[span][recency];
    const locked = this.isPeriodLocked(period);
    const scores = this.props.scores[period].scores;
    const gameCount = this.props.scores[period].gameCount;
    const pluralChar = gameCount === 1 ? "" : "s";
    const dayCount = Constants.SCORE_PERIOD_DAY_COUNTS[period];
    const ownStanding = this.getOwnStanding();

    let footerString;
    if (dayCount) {
      footerString = `${gameCount} eligible player${pluralChar}`;
    } else {
      footerString = `${gameCount} completed game${pluralChar}`;
    }

    // The season in progress is the one panel with a deadline attached to it, so
    // the countdown rides on its footer rather than sitting on the profile,
    // where it was a fact about the site shown in a place about one player. The
    // day being played counts itself, so the final Sunday reads as one day left
    // rather than none.
    if (period === Constants.SCORES_THIS_SEASON) {
      const daysRemaining = Seasons.getSeasonStatus(Utils.getDateStringToday()).daysRemaining;
      if (daysRemaining > 0) {
        const dayNoun = daysRemaining === 1 ? "day" : "days";
        footerString += ` · ${daysRemaining} ${dayNoun} left`;
      }
    }

    return (
      <div className="scoreboard">
        {/* The panel's name, then the two selectors that choose what it shows.
            The name is the panel's, not the period's -- the selected options
            already say which period, so a heading naming that as well would be
            saying it twice, which is what the period title here used to do. The
            modal pins the block, so the controls stay reachable while the board
            scrolls under them. */}
        <div className="scoreboard-header">
          <div className="scoreboard-title-bar">
            <div className="scoreboard-title">
              Leaderboards
            </div>
          </div>
          <div className="scoreboard-selector" role="group" aria-label="Period length">
            { Constants.SCORE_SPANS.map((candidate) => this.renderOption(
              candidate,
              Constants.SCORE_SPAN_LABELS[candidate],
              candidate === span,
              this.isSpanAvailable(candidate),
              this.handleSpanClick
            )) }
          </div>
          <div className="scoreboard-selector" role="group" aria-label="Which period">
            { Constants.SCORE_RECENCIES.map((candidate) => this.renderOption(
              candidate,
              this.getRecencyLabel(span, candidate),
              candidate === recency,
              this.isRecencyAvailable(span, candidate),
              this.handleRecencyClick
            )) }
          </div>
        </div>
        {locked
          ? this.renderLockedMessage(period)
          : <>
              {scores && scores.map((score, i) => this.renderRow(score, i))}
              {ownStanding &&
                <div className="scoreboard-own-standing">
                  {this.renderRow(ownStanding, scores.length)}
                </div>
              }
            </>
        }
        {gameCount !== undefined &&
          <div className="scoreboard-footer">
            {footerString}
          </div>
        }
      </div>
    );
  }
}

export default Scoreboard;
